import React, { useState, useEffect } from 'react';
import TaskItem from './TaskItem';
import { fetchUserGroups, createGroup } from '../services/groupService';
import { fetchRoutines, createRoutine } from '../services/taskService'; // Import routine services
import { CATEGORIES, getCategoryColor, getCategoryMeta } from '../constants/categories';
import './DayView.css';
import SharedTabView from './SharedTabView';


function DayView({
  selectedDate,
  tasks = {},
  onDeleteTask,
  onTogglePriority,
  onMapClick,
  onSuggestClick,
  onAddTask,
  userLocation, // New prop
}) {
  const [routineTasks, setRoutineTasks] = useState([]); // Empty default
  const [newRoutine, setNewRoutine] = useState('');
  const [manualTaskText, setManualTaskText] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualPriority, setManualPriority] = useState(false);
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState('');

  useEffect(() => {
    loadGroups();
    loadRoutines(); // Load routines on mount
  }, []);

  const loadGroups = async () => {
    try {
      const data = await fetchUserGroups();
      setGroups(data?.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const loadRoutines = async () => {
    try {
      const data = await fetchRoutines();
      setRoutineTasks(data || []);
    } catch (err) {
      console.error('Error fetching routines:', err);
    }
  };

  if (!selectedDate) return null;
  const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
  const dayTasks = tasks[dateStr] || [];

  const handleAddRoutine = async () => {
    if (newRoutine.trim()) {
      try {
        await createRoutine(newRoutine.trim()); // Call backend
        setNewRoutine('');
        loadRoutines(); // Refresh list
      } catch (err) {
        console.error("Failed to create routine", err);
      }
    }
  };

  const handleDeleteRoutine = (id) => {
    // Optimistic update or unimplemented backend delete
    setRoutineTasks((prev) => prev.filter((r) => r.id !== id));
  };

  const handleAddManualTask = () => setShowManualInput(true);

  const handleSubmitManualTask = () => {
    if (manualTaskText.trim()) {
      onAddTask(manualTaskText.trim(), dateStr, manualPriority);
      setManualTaskText('');
      setManualPriority(false);
      setShowManualInput(false);
    }
  };

  const handleCancelManualTask = () => {
    setManualTaskText('');
    setManualPriority(false);
    setShowManualInput(false);
  };

  const handleCreateGroup = async () => {
    if (newGroup.trim()) {
      try {
        await createGroup(newGroup.trim());
        setNewGroup('');
        await loadGroups();
      } catch (err) {
        console.error('Error creating group:', err);
      }
    }
  };

  // Group the day's tasks by category for display. We tag each task with its
  // original index in `dayTasks` first, because delete / priority handlers in
  // App.js index into that flat array — regrouping must not change those indices.
  const groupedTasks = (() => {
    const byCategory = new Map(CATEGORIES.map((c) => [c.name, []]));
    dayTasks.forEach((task, i) => {
      const cat = getCategoryMeta(task.category).name; // falls back to "Other"
      byCategory.get(cat).push({ task, originalIndex: i });
    });
    // Keep CATEGORIES order (Shopping → … → Other); drop empty sections.
    return CATEGORIES
      .map((c) => ({ name: c.name, items: byCategory.get(c.name) }))
      .filter((g) => g.items.length > 0);
  })();

  const renderTaskTab = () => (
    <div className="category-section">
      {dayTasks.length === 0 && <p className="no-tasks-msg">No tasks for this day.</p>}
      {groupedTasks.map((group) => (
        <div key={group.name} className="category-group">
          <div className="category-group-header">
            <span className="category-group-dot" style={{ background: getCategoryColor(group.name) }} />
            <h4 style={{ color: getCategoryColor(group.name) }}>{group.name}</h4>
            <span className="category-group-count">{group.items.length}</span>
          </div>
          <div className="task-list">
            {group.items.map(({ task, originalIndex }) => (
              <TaskItem
                key={`${dateStr}-${originalIndex}`}
                task={task}
                index={originalIndex}
                taskDate={dateStr}
                onDelete={onDeleteTask}
                onTogglePriority={onTogglePriority}
                onMapClick={onMapClick}
                onSuggestClick={onSuggestClick}
                userLocation={userLocation}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderRoutineTab = () => (
    <div className="category-section">
      <div className="section-header-premium">
        <div>
          <h4>Daily Routine</h4>
          <p className="subtitle">Tasks that reset automatically every day</p>
        </div>
      </div>

      <div className="routine-grid">
        {routineTasks.map((task) => (
          <div key={task.id || Math.random()} className="routine-card">
            <div className="routine-content">
              <label className="checkbox-container">
                <input type="checkbox" />
                <span className="checkmark"></span>
              </label>
              <span className="routine-text">{task.name || task}</span>
            </div>
            <button
              onClick={() => handleDeleteRoutine(task.id)}
              className="delete-routine-btn"
              aria-label="Delete routine"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        ))}
      </div>

      <div className="add-routine-wrapper">
        <div className="routine-input-group">
          <input
            type="text"
            value={newRoutine}
            onChange={(e) => setNewRoutine(e.target.value)}
            placeholder="Add a new daily habit..."
            className="routine-input-modern"
            onKeyDown={(e) => e.key === 'Enter' && handleAddRoutine()}
          />
          <button onClick={handleAddRoutine} className="add-routine-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const renderSharedTab = () => (
    <div className="category-section">
      <h4>👥 My Groups</h4>
      <div className="task-card">
        <ul className="list-none pl-0">
          {groups.map((group) => (
            <li key={group.id} className="py-1">👥 {group.name}</li>
          ))}
        </ul>
        <div className="flex gap-2 mt-4">
          <input
            type="text"
            placeholder="New group name"
            value={newGroup}
            onChange={(e) => setNewGroup(e.target.value)}
            className="flex-1 border border-gray-300 px-3 py-2 rounded-md"
          />
          <button onClick={handleCreateGroup} className="map-btn">➕ Add Group</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="day-view-container">
      {/* The Tasks/Routine/Shared toggle was removed — the top AppNav tab strip
          already covers Daily (routines) and Team (shared). This view shows the
          day's task list directly. */}
      {renderTaskTab()}
    </div>
  );
}

export default DayView;
